import { Box, Button, debounce, Typography } from '@mui/material'
import { SceneManager } from '../../visualization/sceneManager'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { createSelector } from '@reduxjs/toolkit'
import { ConstructionTab } from '../../reducers/construction'
import { ClusteringManager } from '../../visualization/clusteringManager'
import { AbstractMesh, Matrix, Vector3 } from '@babylonjs/core'
import {
  CodeValidationDiscipline,
  CodeValidationDisciplineColor,
  CodeValidationStatus,
} from '../../models/Validation'
import { getEnums } from '../../utils/Common'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { getValidationItemName } from '../../utils/ConstructionUtil'
import { selectCodeValidationItem } from '../../actions/validation'
import { EventType } from '../../utils/EventEmitter'

const validationIssuesClustersSelector = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.validation.codeValidationRules,
  ],
  (constructionTab, validationResults) => {
    if (constructionTab !== ConstructionTab.Validation) {
      return null
    }

    const clusteringManager = ClusteringManager.getInstance()
    const disciplinesClusters = new Map<
      CodeValidationDiscipline,
      { meshes: AbstractMesh[]; isVisible: boolean }[]
    >()
    getEnums(CodeValidationDiscipline).map(([key, discipline], index) => {
      const disciplineResults = validationResults[discipline]
      const elementIds = disciplineResults
        .filter(
          (result) =>
            result.status === CodeValidationStatus.Failed &&
            result.elementsIds?.length,
        )
        .flatMap((result) => result.elementsIds)
      const disciplineClusters = clusteringManager.createClusters(
        discipline,
        elementIds,
      )
      if (disciplineClusters) {
        disciplinesClusters.set(discipline, disciplineClusters.clusters)
      }
    })

    return disciplinesClusters
  },
)

type ClusterCenters3D = {
  discipline: CodeValidationDiscipline
  clusters: {
    center3D: Vector3
    count: number
    isVisible: boolean
    itemIds: string[]
  }[]
}

type DisciplinesClusters2D = {
  discipline: CodeValidationDiscipline
  clusters: {
    id: string
    centerX: number
    centerY: number
    count: number
    isVisible: boolean
    itemIds: string[]
  }[]
}

type DisciplineClusterUpdate = {
  discipline: CodeValidationDiscipline
  clusters: { meshes: AbstractMesh[]; isVisible: boolean }[]
}

const ValidationIssuesClusterComponent = () => {
  const dispatch = useDispatch<any>()
  const validationIssuesClusters = useSelector(validationIssuesClustersSelector)

  const [clusterCenters3D, setClusterCenters3D] = useState<ClusterCenters3D[]>(
    [],
  )
  const [disciplinesClusters, setDisciplinesClusters] = useState<
    DisciplinesClusters2D[]
  >([])
  const [selectedClusters, setSelectedClusters] = useState<string[]>([])
  const [clustersViewUpdated, setClustersViewUpdated] = useState(false)

  const ITEM_BATCH_SIZE = 100
  const [visibleItemCounts, setVisibleItemCounts] = useState<
    Record<string, number>
  >({})

  const sceneManager = useMemo(() => SceneManager.getInstance(), [])
  const scene = sceneManager.getScene()
  const engine = sceneManager.getEngine()
  const camera = sceneManager.getCamera()
  const eventEmitter = sceneManager.getEventEmitter()

  useEffect(() => {
    if (!eventEmitter) return

    const handleClusterUpdated = (
      disciplineClusters: DisciplineClusterUpdate | null,
    ) => {
      if (!disciplineClusters) return
      const clusterCenters = disciplineClusters.clusters.map((cluster) => {
        const center = cluster.meshes
          .reduce(
            (acc, mesh) => acc.add(mesh.getAbsolutePosition()),
            Vector3.Zero(),
          )
          .scale(1 / cluster.meshes.length)

        return {
          center3D: center,
          count: cluster.meshes.length,
          isVisible: cluster.isVisible,
          itemIds: cluster.meshes.map((mesh) => mesh.id),
        }
      })

      setClusterCenters3D((prev) => {
        const index = prev.findIndex(
          (p) => p.discipline === disciplineClusters.discipline,
        )
        const updated = [...prev]
        if (index !== -1) {
          updated[index] = {
            discipline: disciplineClusters.discipline,
            clusters: clusterCenters,
          }
        } else {
          updated.push({
            discipline: disciplineClusters.discipline,
            clusters: clusterCenters,
          })
        }
        return updated
      })
    }

    const handleClustersViewUpdated = () => setClustersViewUpdated(true)

    eventEmitter.on(
      EventType.CodeValidationClustersUpdated,
      handleClusterUpdated,
    )
    eventEmitter.on(
      EventType.CodeValidationClustersViewUpdated,
      handleClustersViewUpdated,
    )

    return () => {
      eventEmitter.off(
        EventType.CodeValidationClustersUpdated,
        handleClusterUpdated,
      )
      eventEmitter.off(
        EventType.CodeValidationClustersViewUpdated,
        handleClustersViewUpdated,
      )
      ClusteringManager.dispose()
    }
  }, [eventEmitter])

  useEffect(() => {
    if (!validationIssuesClusters) return

    const updatedCenters = Array.from(validationIssuesClusters.entries()).map(
      ([discipline, clusters]) => ({
        discipline,
        clusters: clusters.map((cluster) => {
          const center = cluster.meshes
            .reduce(
              (acc, mesh) => acc.add(mesh.getAbsolutePosition()),
              Vector3.Zero(),
            )
            .scale(1 / cluster.meshes.length)

          return {
            center3D: center,
            count: cluster.meshes.length,
            isVisible: cluster.isVisible,
            itemIds: cluster.meshes.map((mesh) => mesh.id),
          }
        }),
      }),
    )

    setClusterCenters3D(updatedCenters)
  }, [validationIssuesClusters])

  useEffect(() => {
    if (clusterCenters3D.length) update2DClusterView()
  }, [clusterCenters3D])

  useEffect(() => {
    if (clustersViewUpdated) {
      update2DClusterView()
      setClustersViewUpdated(false)
    }
  }, [clustersViewUpdated])

  const update2DClusterView = useCallback(
    debounce(() => {
      if (!scene || !camera || !engine) return

      const updatedClusters = clusterCenters3D.map(
        ({ discipline, clusters }) => ({
          discipline,
          clusters: clusters.map((cluster) => {
            const screen = Vector3.Project(
              cluster.center3D,
              Matrix.Identity(),
              scene.getTransformMatrix(),
              camera.viewport.toGlobal(
                engine.getRenderWidth(true),
                engine.getRenderHeight(true),
              ),
            )

            return {
              id: uuid(),
              centerX: screen.x,
              centerY: screen.y,
              count: cluster.count,
              isVisible: cluster.isVisible,
              itemIds: cluster.itemIds,
            }
          }),
        }),
      )

      setDisciplinesClusters(updatedClusters)
    }, 50), // 50ms debounce
    [clusterCenters3D, scene, camera, engine],
  )

  const handleClusterClick = (id: string) => {
    setSelectedClusters((prev) =>
      prev.includes(id)
        ? prev.filter((selected) => selected !== id)
        : [...prev, id],
    )

    setVisibleItemCounts((prev) => ({
      ...prev,
      [id]: prev[id] ?? ITEM_BATCH_SIZE,
    }))
  }

  const handleShowIssueClick = (clusterId: string, id: string) => {
    if (!scene) return

    const mesh = scene.getMeshById(id)
    if (mesh) {
      dispatch(selectCodeValidationItem({ item: id }))
      setSelectedClusters((prev) =>
        prev.filter((selected) => selected !== clusterId),
      )
      sceneManager.animateCameraTo(mesh.getAbsolutePosition())
    }
  }

  const handleItemListScroll = (
    e: React.UIEvent<HTMLDivElement>,
    clusterId: string,
    totalItems: number,
  ) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setVisibleItemCounts((prev) => {
        const current = prev[clusterId] || 0
        if (current >= totalItems) return prev
        return {
          ...prev,
          [clusterId]: Math.min(current + ITEM_BATCH_SIZE, totalItems),
        }
      })
    }
  }

  if (!disciplinesClusters.length) {
    return null
  }

  return (
    <>
      {disciplinesClusters.map((disciplineClusters, disciplineIndex) =>
        disciplineClusters.clusters
          .filter((clusterInfo) => clusterInfo.count > 1)
          .map((clusterInfo, index) => (
            <Box key={index} sx={{ display: 'flex' }}>
              <Box
                onClick={() => handleClusterClick(clusterInfo.id)}
                sx={{
                  position: 'absolute',
                  left: `${clusterInfo.centerX}px`,
                  top: `${clusterInfo.centerY}px`,
                  width: '2rem',
                  height: '2rem',
                  borderRadius: '50%',
                  backgroundColor: selectedClusters.includes(clusterInfo.id)
                    ? CodeValidationDisciplineColor[
                        disciplinesClusters[disciplineIndex].discipline
                      ]
                    : 'white',
                  border: `2px solid ${CodeValidationDisciplineColor[disciplinesClusters[disciplineIndex].discipline]}`,
                  color: selectedClusters.includes(clusterInfo.id)
                    ? 'white'
                    : CodeValidationDisciplineColor[
                        disciplinesClusters[disciplineIndex].discipline
                      ],
                  display: clusterInfo.isVisible ? 'flex' : 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'white',
                    backgroundColor:
                      CodeValidationDisciplineColor[
                        disciplinesClusters[disciplineIndex].discipline
                      ],
                    zIndex: 101,
                  },
                }}
              >
                {clusterInfo.count}
              </Box>
              {selectedClusters.includes(clusterInfo.id) && (
                <Box
                  gap='0.3rem'
                  sx={{
                    position: 'absolute',
                    left: `${clusterInfo.centerX}px`,
                    top: `${clusterInfo.centerY}px`,
                    width: '15rem',
                    maxHeight: '20rem',
                    overflowY: 'auto',
                    ml: '2.7rem',
                    backgroundColor: 'white',
                    color:
                      CodeValidationDisciplineColor[
                        disciplinesClusters[disciplineIndex].discipline
                      ],
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: '0px 3px 5px 0px #2F2F2F40',
                    borderRadius: '0.3rem',
                    p: '0.3rem',
                    zIndex: 100,
                  }}
                  onScroll={(e) =>
                    handleItemListScroll(
                      e,
                      clusterInfo.id,
                      clusterInfo.itemIds.length,
                    )
                  }
                >
                  {clusterInfo.itemIds
                    .slice(
                      0,
                      visibleItemCounts[clusterInfo.id] || ITEM_BATCH_SIZE,
                    )
                    .map((itemId, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: '0.1rem solid #FF2F5F',
                          borderRight: '0.1rem solid #FF2F5F',
                          borderBottom: '0.1rem solid #FF2F5F',
                          borderLeft: '0.5rem solid #FF2F5F',
                          borderRadius: '0.3rem',
                          p: '0.3rem',
                          width: 'calc(100% - 1.2rem)',
                        }}
                      >
                        <Typography
                          sx={{ fontSize: '0.7rem', color: '#04002E' }}
                        >
                          {getValidationItemName(itemId)}
                        </Typography>
                        <Button
                          size='small'
                          disableRipple
                          onClick={() =>
                            handleShowIssueClick(clusterInfo.id, itemId)
                          }
                          sx={{
                            fontSize: '0.5rem',
                            border: '0.1rem solid #04002E',
                            borderRadius: '0.3rem',
                            color: '#04002E',
                            p: '0.1rem 0.3rem',
                            '&:hover': {
                              color: 'white',
                              backgroundColor: '#04002E',
                            },
                          }}
                        >
                          Show issue
                        </Button>
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          )),
      )}
    </>
  )
}

export default ValidationIssuesClusterComponent
